import { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ozo_admin_token", data.token);
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EC] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <a href="/" data-testid="admin-back-to-site" className="inline-flex items-center gap-2 text-sm text-[#4A627E] hover:text-[#0077D1] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Website
        </a>

        <div className="bg-white border border-[#0C3A72]/10 p-8 sm:p-10">
          <div className="mb-8">
            <div className="w-12 h-12 bg-[#0077D1]/10 flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#0077D1]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#0C3A72] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Admin Access
            </h1>
            <p className="text-sm text-[#4A627E] mt-2">Sign in to manage contact submissions</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <Label htmlFor="admin-email" className="text-sm font-medium text-[#0C3A72] mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A627E]/50" />
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="admin-password" className="text-sm font-medium text-[#0C3A72] mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A627E]/50" />
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72]"
                />
              </div>
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#0077D1] text-white py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#005BA1] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
